import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import type { Form9Certificate } from '@gng-ehs/shared/types';
import { formatIndexDate } from './pdf-form11-index-client';

export interface Form9IndexItem extends Form9Certificate {
  company_name?: string;
  company_short_code?: string;
  equipment_short_code?: string;
  internal_id_number?: string;
  equipment_name_type?: string;
  swl_value?: number;
  swl_text?: string;
  equipment_size?: string;
  location_in_factory?: string;
  make?: string;
  model?: string;
  inspector_name?: string;
}

export interface Form9IndexExportOptions {
  companyName: string;
  items: Form9IndexItem[];
  periodLabel?: string;
}

interface GroupedLocation {
  location: string;
  categories: {
    category: string;
    items: Form9IndexItem[];
  }[];
}

/**
 * Group Form 9 items by Location and then by Equipment Category.
 */
function groupForm9Items(items: Form9IndexItem[]): GroupedLocation[] {
  const locationMap = new Map<string, Map<string, Form9IndexItem[]>>();

  for (const item of items) {
    const loc = (item.location_in_factory || 'General').trim() || 'General';
    const cat = (item.equipment_name_type || 'Other Equipment').trim() || 'Other Equipment';

    if (!locationMap.has(loc)) {
      locationMap.set(loc, new Map());
    }
    const catMap = locationMap.get(loc)!;
    if (!catMap.has(cat)) {
      catMap.set(cat, []);
    }
    catMap.get(cat)!.push(item);
  }

  const result: GroupedLocation[] = [];
  for (const [location, catMap] of locationMap.entries()) {
    const categories: { category: string; items: Form9IndexItem[] }[] = [];
    for (const [category, catItems] of catMap.entries()) {
      categories.push({ category, items: catItems });
    }
    result.push({ location, categories });
  }

  return result;
}

/**
 * Generate a client-side Form 9 Index PDF (Portrait A4) using the existing equipment index style.
 * Returns a Blob URL for preview / download.
 */
export function generateForm9IndexPDF(options: Form9IndexExportOptions): string {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const { items } = options;

  const PAGE_WIDTH = 210;
  const PAGE_HEIGHT = 297;
  const MARGIN_LEFT = 10;
  const MARGIN_RIGHT = 10;
  const MARGIN_TOP = 10;
  const MARGIN_BOTTOM = 12;

  const TABLE_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT; // 190 mm

  // Column widths (Total = 190 mm):
  // Col 0: Sr.No (12 mm)
  // Col 1: Tag No. (30 mm)
  // Col 2: New ID Number/ Remark (44 mm)
  // Col 3: Description Of Tool (44 mm)
  // Col 4: SWL (20 mm)
  // Col 5: Inspection Date (20 mm)
  // Col 6: Due Date (20 mm)
  const COL_WIDTHS = [12, 30, 44, 44, 20, 20, 20];
  const COL_X = [
    MARGIN_LEFT,
    MARGIN_LEFT + COL_WIDTHS[0],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + COL_WIDTHS[3],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + COL_WIDTHS[3] + COL_WIDTHS[4],
    MARGIN_LEFT + COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + COL_WIDTHS[3] + COL_WIDTHS[4] + COL_WIDTHS[5],
  ];

  const HEADER_HEIGHT = 9;

  const drawTableHeader = (yPos: number): number => {
    // 1. Yellow Background Header (#FFFF00)
    doc.setFillColor(255, 255, 0);
    doc.rect(MARGIN_LEFT, yPos, TABLE_WIDTH, HEADER_HEIGHT, 'F');

    // Header border and column lines
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.35);
    doc.rect(MARGIN_LEFT, yPos, TABLE_WIDTH, HEADER_HEIGHT, 'S');

    for (let i = 1; i < COL_X.length; i++) {
      doc.line(COL_X[i], yPos, COL_X[i], yPos + HEADER_HEIGHT);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);

    const textY = yPos + 6;
    doc.text('Sr.No', COL_X[0] + COL_WIDTHS[0] / 2, textY, { align: 'center' });
    doc.text('Tag No.', COL_X[1] + COL_WIDTHS[1] / 2, textY, { align: 'center' });
    doc.text('New ID Number/ Remark', COL_X[2] + COL_WIDTHS[2] / 2, textY, { align: 'center' });
    doc.text('Description Of Tool', COL_X[3] + COL_WIDTHS[3] / 2, textY, { align: 'center' });
    doc.text('SWL', COL_X[4] + COL_WIDTHS[4] / 2, textY, { align: 'center' });
    doc.text('Inspection Date', COL_X[5] + COL_WIDTHS[5] / 2, textY, { align: 'center' });
    doc.text('Due Date', COL_X[6] + COL_WIDTHS[6] / 2, textY, { align: 'center' });

    return yPos + HEADER_HEIGHT;
  };

  let currentY = drawTableHeader(MARGIN_TOP);

  const grouped = groupForm9Items(items);

  for (const locGroup of grouped) {
    // Check space for Location Banner
    const locBannerHeight = 6.5;
    if (currentY + locBannerHeight + 10 > PAGE_HEIGHT - MARGIN_BOTTOM) {
      doc.addPage('a4', 'portrait');
      currentY = drawTableHeader(MARGIN_TOP);
    }

    // Draw Location Banner (Yellow #FFFF00)
    doc.setFillColor(255, 255, 0);
    doc.rect(MARGIN_LEFT, currentY, TABLE_WIDTH, locBannerHeight, 'F');
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.35);
    doc.rect(MARGIN_LEFT, currentY, TABLE_WIDTH, locBannerHeight, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text(locGroup.location, MARGIN_LEFT + TABLE_WIDTH / 2, currentY + 4.5, { align: 'center' });

    currentY += locBannerHeight;

    for (const catGroup of locGroup.categories) {
      // Check space for Tool Category Sub-header
      const catBannerHeight = 5.5;
      if (currentY + catBannerHeight + 8 > PAGE_HEIGHT - MARGIN_BOTTOM) {
        doc.addPage('a4', 'portrait');
        currentY = drawTableHeader(MARGIN_TOP);
      }

      // Draw Tool Category Sub-header (Peach #FFE0B2)
      doc.setFillColor(255, 224, 178);
      doc.rect(MARGIN_LEFT, currentY, TABLE_WIDTH, catBannerHeight, 'F');
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.35);
      doc.rect(MARGIN_LEFT, currentY, TABLE_WIDTH, catBannerHeight, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(catGroup.category, MARGIN_LEFT + 3, currentY + 3.8);

      currentY += catBannerHeight;

      // Render items in this category
      catGroup.items.forEach((item, idx) => {
        const srNo = String(idx + 1);
        const tagNo = item.internal_id_number || item.equipment_short_code || item.certificate_number || '—';
        const toolDesc = item.equipment_name_type || catGroup.category;
        
        let swl = item.maximum_safe_working_load || '—';
        if (!item.maximum_safe_working_load && item.swl_text) {
          swl = item.swl_text;
        } else if (!item.maximum_safe_working_load && item.swl_value !== undefined && item.swl_value !== null) {
          swl = `${item.swl_value} Ton`;
        }

        const inspDate = formatIndexDate(item.examination_date);
        const dueDate = formatIndexDate(item.next_inspection_date);

        const remarkText = item.repairs_required || item.other_particulars || item.equipment_size || [item.make, item.model].filter(Boolean).join(' ');

        // Calculate dynamic height based on text wrapping
        doc.setFontSize(7.5);
        const splitRemark = doc.splitTextToSize(remarkText, COL_WIDTHS[2] - 3);
        const splitDesc = doc.splitTextToSize(toolDesc, COL_WIDTHS[3] - 3);
        const maxLines = Math.max(splitRemark.length, splitDesc.length, 1);
        const rowHeight = Math.max(5.5, maxLines * 3.8 + 2);

        // Check page break
        if (currentY + rowHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
          doc.addPage('a4', 'portrait');
          currentY = drawTableHeader(MARGIN_TOP);
        }

        // Draw row borders
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.25);
        doc.rect(MARGIN_LEFT, currentY, TABLE_WIDTH, rowHeight, 'S');

        for (let i = 1; i < COL_X.length; i++) {
          doc.line(COL_X[i], currentY, COL_X[i], currentY + rowHeight);
        }

        const midY = currentY + rowHeight / 2 + 1.2;

        // Col 0: Sr.No
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(0, 0, 0);
        doc.text(srNo, COL_X[0] + COL_WIDTHS[0] / 2, midY, { align: 'center' });

        // Col 1: Tag No.
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(tagNo, COL_X[1] + 2, midY);

        // Col 2: New ID Number / Remark
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        if (splitRemark.length > 1) {
          doc.text(splitRemark, COL_X[2] + 2, currentY + 3.5);
        } else {
          doc.text(remarkText || '—', COL_X[2] + 2, midY);
        }

        // Col 3: Description Of Tool
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        if (splitDesc.length > 1) {
          doc.text(splitDesc, COL_X[3] + 2, currentY + 3.5);
        } else {
          doc.text(toolDesc, COL_X[3] + 2, midY);
        }

        // Col 4: SWL
        doc.text(swl, COL_X[4] + COL_WIDTHS[4] / 2, midY, { align: 'center' });

        // Col 5: Inspection Date
        doc.text(inspDate || '—', COL_X[5] + COL_WIDTHS[5] / 2, midY, { align: 'center' });

        // Col 6: Due Date
        doc.text(dueDate || '—', COL_X[6] + COL_WIDTHS[6] / 2, midY, { align: 'center' });

        currentY += rowHeight;
      });
    }
  }

  const pdfBlob = doc.output('blob');
  return URL.createObjectURL(pdfBlob);
}

/**
 * Generate and download Form 9 Index as an Excel spreadsheet (.xlsx).
 */
export function exportForm9IndexToExcel(options: Form9IndexExportOptions): void {
  const { companyName, items, periodLabel = 'Last_1_Year' } = options;

  const excelRows: Record<string, unknown>[] = [];
  const grouped = groupForm9Items(items);

  for (const locGroup of grouped) {
    // Location Banner Header Row
    excelRows.push({
      'Sr.No': '',
      'Tag No.': `[ Area / Department: ${locGroup.location} ]`,
      'New ID Number/ Remark': '',
      'Description Of Tool': '',
      'SWL': '',
      'Inspection Date': '',
      'Due Date': '',
    });

    for (const catGroup of locGroup.categories) {
      // Category Sub-header Row
      excelRows.push({
        'Sr.No': '',
        'Tag No.': `--- ${catGroup.category} ---`,
        'New ID Number/ Remark': '',
        'Description Of Tool': '',
        'SWL': '',
        'Inspection Date': '',
        'Due Date': '',
      });

      catGroup.items.forEach((item, idx) => {
        const remark = item.repairs_required || item.other_particulars || item.equipment_size || [item.make, item.model].filter(Boolean).join(' ');

        let swl = item.maximum_safe_working_load || '—';
        if (!item.maximum_safe_working_load && item.swl_text) {
          swl = item.swl_text;
        } else if (!item.maximum_safe_working_load && item.swl_value !== undefined && item.swl_value !== null) {
          swl = `${item.swl_value} Ton`;
        }

        excelRows.push({
          'Sr.No': idx + 1,
          'Tag No.': item.internal_id_number || item.equipment_short_code || '',
          'New ID Number/ Remark': remark,
          'Description Of Tool': item.equipment_name_type || catGroup.category,
          'SWL': swl,
          'Inspection Date': formatIndexDate(item.examination_date) || '',
          'Due Date': formatIndexDate(item.next_inspection_date) || '',
        });
      });
    }
  }

  const worksheet = XLSX.utils.json_to_sheet(excelRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Form 9 Index');

  worksheet['!cols'] = [
    { wch: 8 },  // Sr.No
    { wch: 25 }, // Tag No.
    { wch: 35 }, // New ID Number/ Remark
    { wch: 35 }, // Description Of Tool
    { wch: 14 }, // SWL
    { wch: 16 }, // Inspection Date
    { wch: 16 }, // Due Date
  ];

  const cleanCompanyName = (companyName || 'Company').replace(/[^a-zA-Z0-9_-]/g, '_');
  const todayStr = new Date().toISOString().split('T')[0];
  const filename = `Form9_Index_${cleanCompanyName}_${periodLabel}_${todayStr}.xlsx`;

  XLSX.writeFile(workbook, filename);
}
